import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api, Goal, Match } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { Flag, refDisplay } from '../components/TeamBadge';
import { formatKickoff } from '../lib/format';
import { venue } from '../lib/venues';
import { H2, Muted, BackLink, Pill } from '../components/ui';

const Scoreboard = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  padding: 22px 14px;
  text-align: center;
  .side { display: flex; flex-direction: column; align-items: center; gap: 8px; }
  .nm { font-weight: 700; }
  .sc { font-size: 2.4rem; font-weight: 800; }
`;

const Goals = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
  font-size: 0.85rem;
  .col:last-child { text-align: right; }
  .g { color: ${({ theme }) => theme.colors.textMuted}; }
`;

const VenueCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  margin-top: 8px;
  .name { font-weight: 700; font-size: 1.05rem; }
`;

function goalLine(g: Goal): string {
  const tag = g.penalty ? ' (p)' : g.owngoal ? ' (e/c)' : '';
  return `${g.name} ${g.minute ?? ''}'${tag}`;
}

function statusLabel(m: Match): string {
  if (m.status === 'live') return 'EN JUEGO';
  if (m.status === 'finished' && m.score) return 'Finalizado';
  return formatKickoff(m.kickoffUtc);
}

export function MatchDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const state = useAsync(() => api.match(id), [id], `/match/${id}`);

  return (
    <>
      <BackLink onClick={() => navigate(-1)}>← Volver</BackLink>
      <Async state={state}>
        {(d) => {
          const m = d.match;
          const t1 = refDisplay(m.team1);
          const t2 = refDisplay(m.team2);
          const v = venue(m.ground);
          return (
            <>
              <div style={{ marginBottom: 10, display: 'flex', gap: 8 }}>
                <Pill>{m.phase === 'group' ? `Grupo ${m.group}` : m.stage?.label}</Pill>
                <Pill>{m.round}</Pill>
              </div>

              <Scoreboard>
                <div className="side">
                  <Flag team={t1.team} size={44} />
                  <div className="nm">{t1.text}</div>
                </div>
                <div>
                  <div className="sc">{m.score ? `${m.score.ft[0]}–${m.score.ft[1]}` : 'vs'}</div>
                  <Muted>{statusLabel(m)}</Muted>
                  {m.score?.ht && (
                    <Muted>
                      ET: {m.score.ht[0]}–{m.score.ht[1]}
                    </Muted>
                  )}
                </div>
                <div className="side">
                  <Flag team={t2.team} size={44} />
                  <div className="nm">{t2.text}</div>
                </div>
              </Scoreboard>

              {(m.goals1.length > 0 || m.goals2.length > 0) && (
                <Goals>
                  <div className="col">
                    {m.goals1.map((g, i) => (
                      <div className="g" key={i}>
                        {goalLine(g)}
                      </div>
                    ))}
                  </div>
                  <div className="col">
                    {m.goals2.map((g, i) => (
                      <div className="g" key={i}>
                        {goalLine(g)}
                      </div>
                    ))}
                  </div>
                </Goals>
              )}

              <H2>Sede</H2>
              {v ? (
                <VenueCard>
                  <div className="name">{v.stadium}</div>
                  <Muted>
                    {v.city}, {v.country} {v.flag} · Capacidad ~{v.capacity.toLocaleString('es-AR')}
                  </Muted>
                </VenueCard>
              ) : (
                <Muted>{m.ground ?? 'Sede a confirmar'}</Muted>
              )}

              <H2>Horario</H2>
              <Muted>{formatKickoff(m.kickoffUtc)} (hora local de tu dispositivo)</Muted>
            </>
          );
        }}
      </Async>
    </>
  );
}
